/**
 * Improvements — operator-improvement signals (ADR-026 WS3).
 *
 * View (any authed role) of every improvement an operator asked for: the
 * verbatim request, the owning capability it was routed to, its lifecycle
 * status, and the PR link once the owner proposes the change. This is how an
 * operator sees their request reach the right owner and land as a PR — instead
 * of dead-ending at the Brain.
 *
 * #388 Phase 1: surface stale "open" items and let an operator Decline one.
 * Phase 3: a chat back-link. Phase 4 (operator engage): per-item actions to
 * Discuss (open a seeded chat with the owning agent), Take next step (have the
 * owner act on the recommended next action now), or Add note — so a stalled
 * "open" item is recoverable by the operator, not a dead end.
 *
 * Polls GET /api/improvements every 15s. The api client handles silent token
 * refresh; an UnauthorizedError here means the console session is truly gone.
 */
import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { api, UnauthorizedError } from '../api/client'
import type { Improvement, ReconcilerHealth } from '../api/client'
import { relativeTime } from '../utils/relativeTime'

const POLL_INTERVAL_MS = 15_000

// An "open" request older than this with no movement is flagged as stale —
// nobody is driving it (see #388). Operators can Decline it or chase the owner.
const STALE_DAYS = 3
const STALE_MS = STALE_DAYS * 24 * 60 * 60 * 1000

// ADR-122 D3: `deployed` and `verified` belong here. The store has always
// defined them (IMPROVEMENT_STATUSES) but neither this list nor the backend's
// whitelist carried them, so a change that actually SHIPPED had no state on the
// board — it stopped at `merged`, a developer milestone, not at what an operator
// means by done.
//
// `verified` is deliberately NOT a filter pill — see displayStatus below. It is
// an outcome judgement, not a place in the lifecycle, so it has no separate
// badge and nothing to filter by; the verdict itself is already on the row.
const STATUS_FILTERS = [
  'all', 'open', 'delegated', 'proposed', 'merged', 'deployed', 'declined',
] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

/**
 * The lifecycle state to SHOW, which is not always the state stored.
 *
 * `deployed` and `declined` are terminal and win outright: once a change is live
 * (or has been turned down) that is the only thing an operator needs the badge
 * to say, whatever it passed through on the way.
 *
 * `verified` is the case that made this necessary. It ranks after `deployed` in
 * the store, so it displaced it — matthaios showed four live changes badged
 * `verified` and nothing on the row said they had shipped, while clicking
 * through to each one read "Live in production". But `verified` is not a further
 * step along the lifecycle; it is a judgement about whether the change HELPED,
 * and that verdict is already rendered on the row from `outcome`
 * ("no_change — …", "regressed — …"). Showing it in the lifecycle slot therefore
 * added nothing and hid the fact that mattered.
 *
 * Collapsing it loses no information and removes a contradiction between the
 * index and the detail page — the same class of defect as ADR-122 D3a.
 *
 * UPDATE (ADR-122 D3b, owner decision): the STORE has since stopped treating
 * `verified` as a lifecycle status too — update_improvement rejects it, and the
 * fleet-signals migration converts existing rows to `deployed`. So this fold is
 * now legacy compatibility rather than the fix: it keeps a row from a database
 * that has not yet run that migration rendering correctly. Safe to delete only
 * once no store anywhere can emit the value.
 */
export function displayStatus(status: string): string {
  return status === 'verified' ? 'deployed' : status
}

function statusBadge(status: string): React.ReactNode {
  const shown = displayStatus(status)
  const map: Record<string, string> = {
    open:      'bg-amber-100 text-amber-800',
    delegated: 'bg-blue-100 text-blue-800',
    proposed:  'bg-violet-100 text-violet-800',
    merged:    'bg-green-100 text-green-800',
    deployed:  'bg-emerald-100 text-emerald-800',
    declined:  'bg-gray-100 text-gray-500',
  }
  const cls = map[shown] ?? 'bg-gray-100 text-gray-600'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {shown}
    </span>
  )
}

// ADR-122 D5: when the last check could not read this change's PR, say so. The
// status beside this badge is the last state we could CONFIRM, not necessarily
// the current one — and a change nobody can report on must not be presented
// with the same confidence as one we just verified.
function unknownStatusBadge(statusError: string | null | undefined): React.ReactNode {
  if (!statusError) return null

  // ADR-140 D8: a `checks_failing:` marker is a DIFFERENT statement from the
  // read-failure markers below, and rendering it as "status unknown" would be a
  // lie in the wrong direction — we know exactly what is wrong. This is the state
  // spartan #584/#727/#748 sat in for nine days while the board showed three
  // healthy proposals, so it gets the loudest badge on the row and names the
  // check: "blocked" tells an operator nothing, "guard" tells them where to look.
  if (statusError.startsWith('checks_failing:')) {
    const checks = statusError.slice('checks_failing:'.length)
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
        title={
          `This change's PR is open but its checks are FAILING (${checks}). ` +
          'It cannot merge and no agent can fix it by re-proposing — a human has to ' +
          'look. Open the change to see the failure.'
        }
      >
        checks failing{checks ? `: ${checks}` : ''}
      </span>
    )
  }

  // Pre-existing mislabel, fixed here because it is the same defect in the same
  // function: the reconciler also writes `deploy_failed:<conclusion>` and
  // `no_deploy_evidence` into this column, and both fell through to the
  // "cannot read <repo>" branch below — which parsed the conclusion AS a repo and
  // rendered "This fleet's credentials cannot read failure". A merged change whose
  // DEPLOY failed is not an unreadable one; it is the opposite, a change we know
  // did not ship.
  if (statusError.startsWith('deploy_failed:')) {
    const conclusion = statusError.slice('deploy_failed:'.length)
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
        title={
          `This change merged, but the deploy that should have shipped it ${conclusion}. ` +
          'It is in the repo and NOT running — re-run the deploy.'
        }
      >
        deploy {conclusion || 'failed'}
      </span>
    )
  }
  if (statusError === 'no_deploy_evidence') {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
        title={
          'This change merged, but no deploy evidence could be obtained for it — ' +
          'so whether it is actually running is unknown. Common for a CORE change, ' +
          'which ships as a rebuilt image rather than a fleet deploy.'
        }
      >
        deploy unverified
      </span>
    )
  }

  const repo = statusError.split(':').slice(1).join(':')
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
      title={
        repo
          ? `This fleet's credentials cannot read ${repo}, so the status shown is the last one confirmed.`
          : 'The status shown is the last one confirmed.'
      }
    >
      status unknown
    </span>
  )
}

// ADR-122 D3: the status on this board is now resolved from GitHub per request,
// so the stored column is a cache rather than the truth — that is what stopped
// matthaios #243 reading `proposed` for an hour after it was provably live.
//
// This badge is the honest half of that: when a row could NOT be resolved (a
// GitHub read failed, or it fell past the per-request cap) it keeps its cached
// value, and the operator is told so instead of the staleness being silent.
// `live` and `n/a` render nothing — a badge on every healthy row would be noise,
// and there is nothing stale about a request with no PR yet.
function unconfirmedStatusBadge(
  statusSource: string | null | undefined,
  statusError: string | null | undefined,
): React.ReactNode {
  // status_error already says the stronger version of this; don't say it twice.
  if (statusSource !== 'stored' || statusError) return null
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
      title="This status is the last cached value — GitHub could not be checked on this load. Open the change to see its live state."
    >
      not just checked
    </span>
  )
}

// Not-yet-resolved states an operator can act on (decline). proposed/merged are
// already moving / done; declined is terminal.
//
// This gates the ENGAGE lane — discuss it, have the owner take the next step —
// and stays exactly as it was. A change with a live pull request does not want
// an agent re-driving it.
function isActionable(status: string): boolean {
  return status === 'open' || status === 'delegated'
}

// bannerGroup classifies proposed rows for the banner: awaiting when the pull
// request is confirmed open and not a core change, unverified when a
// pull-request link exists but its state was not established, or neither.
export function bannerGroup(it: Improvement): 'awaiting' | 'unverified' | null {
  if (it.status !== 'proposed') return null
  if (it.pr_state === 'open') return it.owned_by_this_fleet === false ? null : 'awaiting'
  if (!it.pr_url || /^https?:\/\/github\.com\/[^/]+\/[^/]+\/issues\/\d+(?:[?#]|$)/i.test(it.pr_url) || it.pr_state === 'none' || it.pr_state === 'merged' || it.pr_state === 'closed_unmerged') return null
  return it.status_source === undefined || it.status_source === 'stored' ? 'unverified' : null
}

// canDismiss — may an operator settle this row by hand?
//
// SPLIT OUT FROM isActionable BECAUSE IT IS A DIFFERENT QUESTION, and conflating
// them left a fleet owner with no way out on 2026-09-18. athena's
// imp_POlPvXpIIhZb_A read `proposed` while its pull request had been closed
// without merging four days earlier. Only a person may move a change to
// `declined` — and both places a person could have done it refused: this list
// showed no Dismiss because the status was `proposed` ("already moving"), and
// the PR review page hides Deny once the PR is not open. The row was not moving,
// it was stuck, and the workflow's editor was held shut behind it.
//
// Dismissable when there is demonstrably nothing left to review: the pull
// request is closed unmerged, the linked branch has no pull request, or no pull
// request was ever linked. All are read
// from EVIDENCE, never from absence — an unset `pr_state` means live resolution
// could not establish one (past the 25-row cap, or GitHub unreachable), which is
// emphatically not a closed PR. Guessing there would offer an operator a Dismiss
// for a change under active review, which is the same bug pointing the other
// way.
//
// The engage buttons stay hidden for these rows on purpose: "take next step" on
// a proposal whose PR was deliberately denied would re-drive work the operator
// had just rejected.
function canDismiss(it: Improvement): boolean {
  if (isActionable(it.status)) return true
  if (it.status !== 'proposed') return false
  return it.pr_state === 'closed_unmerged' || it.pr_state === 'none' || !it.pr_url
}

// prNumber extracts the "#123" number from a GitHub PR url (…/pull/123) so the
// Improvements list can show WHICH PR an improvement links to, not just a bare
// "Review PR" (operators couldn't tell one proposed PR from another). Returns ''
// for a non-PR / unparseable url so the caller falls back to plain "Review PR".
function prNumber(prUrl: string | null): string {
  const m = prUrl?.match(/\/pull\/(\d+)/)
  return m ? m[1] : ''
}

/** Trim a verbatim request to a scannable line for the stale banner's list. */
function shortText(s: string, max = 70): string {
  const one = s.replace(/\s+/g, ' ').trim()
  return one.length > max ? one.slice(0, max - 1) + '…' : one
}

function isStale(it: Improvement): boolean {
  if (it.status !== 'open') return false
  const created = Date.parse(it.created_at)
  return !Number.isNaN(created) && Date.now() - created > STALE_MS
}

// How long since a successful sweep before the board stops trusting its own
// statuses. The sweep runs every ten minutes, so THIRTY covers two missed turns
// plus slack — long enough that a restart or a single broker blip does not cry
// wolf, short enough that nobody discovers a stall seven weeks later.
const RECONCILER_STALE_MS = 30 * 60 * 1000
// Statuses tolerated while the last success is younger than RECONCILER_STALE_MS. Explicit, so a new status warns until someone decides otherwise.
const RECONCILER_TOLERATED_STATUSES: readonly ReconcilerHealth['status'][] = ['ok', 'no_token', 'error']

/**
 * What the board should SAY about the sweep that advances its rows — null when
 * there is nothing to say.
 *
 * THE DEFECT THIS EXISTS FOR. The stored `status` is a cache whose only writer
 * is the reconciler sweep. It died on athena and matthaios in late July 2026;
 * the first anyone knew was a fleet owner reporting on 2026-09-18 that the
 * workflow editor would not let them propose a change, seven weeks later, with
 * 153 of athena's rows frozen at `proposed` and 17 workflows locked behind them.
 * The sweep warned on stdout the whole time, into a firehose nobody reads.
 *
 * FOUR CASES, and the two absences are the ones that matter:
 *
 *   - `undefined` — this Mission Control did not send the field. Say NOTHING:
 *     an older backend is not evidence about the sweep, and inventing a warning
 *     from its silence would train operators to ignore this banner.
 *   - `null` — the fleet answered, and the answer is that the sweep has never
 *     reported. That is the loudest case available, not a blank.
 *   - failing for 30 minutes or more, or never succeeded — stalled. A failing
 *     sweep with a recent success is tolerated so one broker blip does not
 *     cry wolf; this follows the 2026-10-07 spartan blip.
 *   - ok and recent — nothing to say.
 */
export function reconcilerWarning(
  health: ReconcilerHealth | null | undefined,
  now: number = Date.now(),
): { headline: string; detail: string } | null {
  if (health === undefined) return null
  if (health === null) {
    return {
      headline: 'Change statuses are not being checked',
      detail:
        'This fleet has never reported a status sweep, so a change shown as "proposed" may ' +
        'have merged or been closed long ago. Open any change to see its live state.',
    }
  }
  const okAt = health.last_ok_at ? Date.parse(health.last_ok_at) : NaN
  const okRecently = !Number.isNaN(okAt) && now - okAt < RECONCILER_STALE_MS
  if (RECONCILER_TOLERATED_STATUSES.includes(health.status) && okRecently) return null

  const since = health.last_ok_at
    ? `Last successful check ${relativeTime(health.last_ok_at)}.`
    : 'It has never completed a successful check.'
  const why: Record<ReconcilerHealth['status'], string> = {
    ok: 'Its last few attempts have not completed.',
    no_token: 'It cannot obtain a GitHub credential, so it is not reading any pull requests.',
    disabled: 'It is not running on this fleet at all — no credential is configured for it.',
    error: `It is failing with an error${health.detail ? `: ${health.detail}` : ''}.`,
  }
  return {
    headline: 'Change statuses are out of date',
    detail: `${why[health.status] ?? why.error} ${since} A change shown as "proposed" may have ` +
      'merged or been closed since. Open any change to see its live state.',
  }
}

export function Improvements() {
  const [items, setItems] = useState<Improvement[]>([])
  // Default to `open`: the board is a worklist, and what an operator opens this
  // page to find is what still needs them (D6). `all` led with a wall of
  // finished work and buried the handful that had stopped moving.
  const [filter, setFilter] = useState<StatusFilter>('open')
  // Set when a stale item is clicked in the banner, to ring it briefly after
  // scrolling — a jump with no visual landing is disorienting.
  const [highlightId, setHighlightId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  // undefined = this Mission Control said nothing about the sweep; null = the
  // fleet said the sweep has never reported. See the banner below.
  const [reconciler, setReconciler] = useState<ReconcilerHealth | null | undefined>(undefined)
  const [awaiting, setAwaiting] = useState<Improvement[]>([])

  const loadAwaiting = useCallback(async () => {
    try {
      const { improvements } = await api.getImprovements('proposed')
      setAwaiting(improvements)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      // Keep the last confirmed rows visible when this supplementary read fails.
    }
  }, [])

  const load = useCallback(async () => {
    try {
      // `deployed` is the one pill whose displayed set is wider than its stored
      // status: displayStatus folds `verified` into it. Asking upstream for
      // status=deployed would therefore hide exactly the live changes that
      // prompted the fold, so fetch unfiltered and narrow here instead. (The
      // upstream default cap is 500; every other pill still filters server-side.)
      const wantsFolded = filter === 'deployed'
      const { improvements, reconciler } = await api.getImprovements(
        filter === 'all' || wantsFolded ? undefined : filter,
      )
      setItems(wantsFolded
        ? improvements.filter((i) => displayStatus(i.status) === 'deployed')
        : improvements)
      // `undefined` distinguishes "this Mission Control did not tell us" from
      // `null`, which is the fleet saying "the sweep has never reported". Only
      // the first is silence about the question rather than an answer to it.
      setReconciler(reconciler === undefined ? undefined : reconciler)
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      setError('Could not load improvements right now.')
    } finally {
      setLoading(false)
    }
  }, [filter])

  useEffect(() => {
    setLoading(true)
    void load()
    void loadAwaiting()
    const t = setInterval(() => {
      void load()
      void loadAwaiting()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(t)
  }, [load, loadAwaiting])

  const handleDecline = useCallback(async (it: Improvement) => {
    const reason = window.prompt(
      `Decline this request?\n\n“${it.verbatim}”\n\nIt will move to "declined". Add a short reason (optional):`,
      '',
    )
    if (reason === null) return // cancelled
    setBusyId(it.id)
    try {
      await api.declineImprovement(it.id, reason.trim())
      await load()
      await loadAwaiting()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      setError('Could not decline that request — try again.')
    } finally {
      setBusyId(null)
    }
  }, [load, loadAwaiting])

  // Engage an item: open a seeded chat with the owning agent ("discuss"), or have
  // it act on the recommended next step now ("next_step"). Both route to the chat
  // so the operator can watch/steer; on success we navigate into that conversation.
  const handleEngage = useCallback(async (it: Improvement, mode: 'discuss' | 'next_step') => {
    setBusyId(it.id)
    try {
      const { conversation_id } = await api.engageImprovement(it.id, mode)
      window.location.href = `/chat?conversation=${encodeURIComponent(conversation_id)}`
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      setError('Could not engage that request — try again.')
      setBusyId(null)
    }
  }, [])

  const handleNote = useCallback(async (it: Improvement) => {
    const note = window.prompt(
      `Add a note to this request?\n\n“${it.verbatim}”`,
      it.operator_note ?? '',
    )
    if (note === null || note.trim() === '') return
    setBusyId(it.id)
    try {
      await api.noteImprovement(it.id, note.trim())
      await load()
      await loadAwaiting()
    } catch (err) {
      if (err instanceof UnauthorizedError) throw err
      setError('Could not save that note — try again.')
    } finally {
      setBusyId(null)
    }
  }, [load, loadAwaiting])

  const staleItems = items.filter(isStale)
  const reconcilerWarn = reconcilerWarning(reconciler)

  // A CHANGE AWAITING APPROVAL IS NOT VISIBLE FROM THIS PAGE, and an auto-proposed
  // one is not visible from anywhere.
  //
  // The default filter is `open`, which is passed upstream as `status=open`, so a
  // `proposed` improvement is not in the response at all on first load. That is
  // right for the worklist — it is argued for at `isActionable`: "proposed/merged
  // are already moving / done" — and it is TRUE ONLY WHEN THE OPERATOR PRESSED
  // PROPOSE. They know they just did it and where to look.
  //
  // ADR-178 D3 broke that assumption: a run reaching `done` proposes BY ITSELF.
  // Nobody pressed anything, so nobody knows to switch to the `proposed` pill.
  //
  // MEASURED, spartan 2026-09-17. Run 20260917T115852Z-c2d2b1 opened PR #1179 and
  // registered imp_CtKDn9i0_1w61A one second later, status `proposed`, source
  // `workflow-studio`, context "reached `done` with 6 passing gate(s); proposed
  // automatically". The fleet owner went looking for that pull request and reported
  // it "nowhere to be found" — while the record, the PR and the Approve & Deploy
  // surface at /improvements/:id/pr all existed and worked.
  //
  // SO THIS IS STATE, NOT A CONTROL. The operator-capabilities standard allows
  // collapsing a MENU behind a link and forbids collapsing STATE, and "a pull
  // request is waiting on you" is state. It renders on EVERY filter, because the
  // whole failure was that finding it required already knowing it was there.
  //
  // FETCHED SEPARATELY rather than by widening the main query: `load` narrows or
  // filters server-side per pill, and folding a second status into it would change
  // what every other pill shows. Same reasoning as the `deployed` fold above, in
  // the other direction.
  const awaitingRows = awaiting.filter((it) => bannerGroup(it) === 'awaiting')
  const unverifiedRows = awaiting.filter((it) => bannerGroup(it) === 'unverified')

  // AUTO-PROPOSED ONES LEAD, because they are the ones nobody has seen. An
  // operator's own proposal is expected; a run's is a surprise, and the context
  // line is where ADR-178 D3 records that it proposed itself.
  const awaitingSorted = [...awaitingRows].sort((a, b) => {
    const auto = (i: Improvement) => (i.context?.includes('proposed automatically') ? 0 : 1)
    return auto(a) - auto(b)
  })

  // The banner used to name a COUNT and an instruction with nothing to click —
  // "1 request has been open > 3 days… Chase the owning capability in chat, or
  // Decline" — leaving the operator to find which request it meant by eye. Name
  // them, and make each one jump to its card (where Discuss / Take next step /
  // Decline already live).
  const jumpToItem = useCallback((id: string) => {
    setHighlightId(id)
    document.getElementById(`imp-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    window.setTimeout(() => setHighlightId((cur) => (cur === id ? null : cur)), 2500)
  }, [])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end gap-3 flex-wrap">
        <div className="flex gap-1">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2 py-1 rounded text-xs font-medium ${
                filter === s ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* AWAITING YOUR APPROVAL. Above the stale banner and on every filter: only
          confirmed-open pull requests owned by this fleet are actionable here. */}
      {awaitingSorted.length > 0 && (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
          <p>
            <span className="font-medium">
              {awaitingSorted.length} change{awaitingSorted.length > 1 ? 's are' : ' is'} proposed
              and waiting on your approval.
            </span>{' '}
            Review the diff and checks in Mission Control — approving never requires GitHub.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {awaitingSorted.map((it) => {
              const auto = it.context?.includes('proposed automatically')
              return (
                <li key={it.id}>
                  <Link
                    to={`/improvements/${encodeURIComponent(it.id)}/pr`}
                    className="underline decoration-emerald-400 hover:text-emerald-950 hover:decoration-emerald-700"
                    title={`Review the diff and checks, and approve or deny${it.pr_url ? ` (${it.pr_url})` : ''}`}
                  >
                    {prNumber(it.pr_url) ? `Review PR #${prNumber(it.pr_url)}` : 'Review the change'}
                  </Link>
                  {' — '}
                  <span>{shortText(it.verbatim, 58)}</span>
                  {it.owner_agent && <span className="text-emerald-700"> · {it.owner_agent}</span>}
                  {/* NAMED, because nobody chose it. An operator who did not press
                      Propose has no reason to expect a pull request to exist, and
                      that surprise is the whole defect this banner closes. */}
                  {auto && (
                    <span className="ml-1 rounded bg-emerald-200 px-1 py-0.5 text-[10px] font-medium">
                      proposed automatically by a run
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {unverifiedRows.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-medium">Could not verify</p>
          <p className="mt-0.5">Mission Control could not confirm whether these pull requests are still open, so they are not counted as waiting on your approval.</p>
          <ul className="mt-1.5 space-y-0.5">
            {unverifiedRows.map((it) => (
              <li key={it.id}>
                <Link to={`/improvements/${encodeURIComponent(it.id)}/pr`} className="underline decoration-amber-400 hover:decoration-amber-700">
                  {prNumber(it.pr_url) ? `PR #${prNumber(it.pr_url)}` : 'Open the change'}
                </Link>
                {' — '}
                <span>{shortText(it.verbatim, 58)}</span>
                {it.owner_agent && <span className="text-amber-700"> · {it.owner_agent}</span>}
                {it.owned_by_this_fleet === false && (
                  <span className="ml-1 rounded bg-amber-200 px-1 py-0.5 text-[10px] font-medium">
                    core change, this fleet cannot read it
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* THE BOARD SAYS WHEN IT CANNOT BE TRUSTED.
          Above everything else, because it qualifies every badge below it: the
          stored status is a cache and this is the only thing that writes it. A
          dead sweep froze two fleets for seven weeks while the board showed
          nothing at all — see reconcilerWarning. */}
      {reconcilerWarn && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          <p className="font-medium">{reconcilerWarn.headline}</p>
          <p className="mt-0.5">{reconcilerWarn.detail}</p>
        </div>
      )}

      {/* Stale-open call to action: these have stopped moving and need a human.
          Each one is clickable — the banner states the problem, the row it jumps
          to carries the actions (Discuss, Take next step, Decline). */}
      {staleItems.length > 0 && filter !== 'declined' && filter !== 'merged' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <p>
            {staleItems.length} request{staleItems.length > 1 ? 's have' : ' has'} been{' '}
            <span className="font-medium">open &gt; {STALE_DAYS} days</span> with no movement.
            Chase the owning capability in chat, or <span className="font-medium">Decline</span> if
            it’s no longer needed.
          </p>
          <ul className="mt-1.5 space-y-0.5">
            {staleItems.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => jumpToItem(it.id)}
                  className="text-left underline decoration-amber-400 hover:text-amber-900 hover:decoration-amber-700"
                  title="Go to this request"
                >
                  · “{shortText(it.verbatim)}” → {it.owner_agent}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      {loading && items.length === 0 ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400">No improvement requests {filter !== 'all' ? `(${filter})` : 'yet'}.</p>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const stale = isStale(it)
            const busy = busyId === it.id
            return (
              <div
                key={it.id}
                id={`imp-${it.id}`}
                className={`bg-white border rounded-lg p-4 scroll-mt-4 transition-shadow ${
                  highlightId === it.id
                    ? 'border-amber-400 ring-2 ring-amber-300'
                    : stale
                      ? 'border-amber-300 ring-1 ring-amber-100'
                      : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 break-words">“{it.verbatim}”</p>
                    <p className="mt-1 text-xs text-gray-500">
                      → <span className="font-medium text-gray-700">{it.owner_agent}</span>
                      {it.operator ? <> · from {it.operator}</> : null}
                      {' · '}
                      {relativeTime(it.created_at)}
                      {stale && <span className="ml-1 text-amber-600 font-medium">· stale</span>}
                    </p>
                    {it.outcome && <p className="mt-1 text-xs text-gray-500">{it.outcome}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {statusBadge(it.status)}
                    {unknownStatusBadge(it.status_error)}
                    {unconfirmedStatusBadge(it.status_source, it.status_error)}
                    {it.pr_url && (
                      <Link
                        to={`/improvements/${encodeURIComponent(it.id)}/pr`}
                        className="text-xs text-blue-600 hover:underline"
                        title={`Review the diff and checks, and approve or deny — without leaving Mission Control (${it.pr_url})`}
                      >
                        {prNumber(it.pr_url) ? `Review PR #${prNumber(it.pr_url)}` : 'Review PR'}
                      </Link>
                    )}
                    {it.conversation_id && (
                      <a
                        href={`/chat?conversation=${encodeURIComponent(it.conversation_id)}`}
                        className="text-xs text-blue-600 hover:underline"
                        title="Open the chat this request came from"
                      >
                        Open chat ↗
                      </a>
                    )}
                  </div>
                </div>

                {it.operator_note && (
                  <p className="mt-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                    <span className="font-medium">Note:</span> {it.operator_note}
                  </p>
                )}

                {/* #388 Phase 4 — operator engage: talk it through, have the owner
                    act now, annotate, or decline. */}
                {canDismiss(it) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
                    {isActionable(it.status) && (
                      <>
                        <button
                          onClick={() => { void handleEngage(it, 'discuss') }}
                          disabled={busy}
                          className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-40"
                          title={`Open a chat with ${it.owner_agent}, seeded with this request`}
                        >
                          💬 Discuss in chat
                        </button>
                        <button
                          onClick={() => { void handleEngage(it, 'next_step') }}
                          disabled={busy}
                          className="text-xs px-2 py-1 rounded bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-40"
                          title={`Have ${it.owner_agent} take the recommended next step now`}
                        >
                          ▶ Take next step
                        </button>
                      </>
                    )}
                    {/* SAY WHY THE BUTTON IS THERE. An operator looking at a row
                        badged `proposed` needs to know it is dismissable because
                        its pull request is gone, not wonder whether dismissing
                        will throw away something under review. */}
                    {!isActionable(it.status) && (
                      <span className="text-xs text-gray-500">
                        {it.pr_state === 'none'
                          ? 'Its link names a branch with no pull request, so there is nothing to review.'
                          : it.pr_url
                            ? 'Its pull request was closed without merging, so nothing is waiting on a review.'
                            : 'No pull request was ever linked, so there is nothing to review.'}
                      </span>
                    )}
                    <button
                      onClick={() => { void handleNote(it) }}
                      disabled={busy}
                      className="text-xs px-2 py-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                    >
                      ✎ Add note
                    </button>
                    <button
                      onClick={() => { void handleDecline(it) }}
                      disabled={busy}
                      className="text-xs px-2 py-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 ml-auto"
                      title="Decline this request (moves it to declined)"
                    >
                      {busy ? '…' : '⛌ Dismiss'}
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
